import { FC, useCallback, useRef, ChangeEvent, useState } from 'react';
import { Stack, Typography, TextField, Button, styled, CircularProgress } from '@mui/material';
import { AttachFileOutlined } from '@mui/icons-material';

import { AttachmentFile } from 'domain/entities/Attachment';
import { useCaseUploadFile } from 'application/attachments/useCases/useCaseUploadFile';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import { Modal } from 'targets/web/components';
import { IconButton, IconContainer, TrashCanIcon } from 'targets/web/components';
import { ACCEPTED_FILE_EXT } from 'targets/web/modules/jobs/components/AttachmentsTable/constants';
import useSnackbar from 'targets/web/modules/dashboard/hooks/useSnackbar';

interface JobCloseoutModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (data: {
    securitySealNumber?: string;
    closeoutNotes?: string;
    attachments: AttachmentFile[];
  }) => void;
  loading?: boolean;
}

const acceptedFiles = ACCEPTED_FILE_EXT.join(', ');

const Container = styled(Stack)(({ theme }) => ({
  flex: '1 0 0',
  alignSelf: 'stretch',
  padding: theme.spacing(6),
}));

const NoAttachments: FC = () => {
  const t = useTranslationPrefix('components.cards.job_closeout_modal');
  return (
    <Container alignItems="center" justifyContent="center" gap={2}>
      <IconContainer padding={2}>
        <AttachFileOutlined sx={{ fontSize: '24px' }} />
      </IconContainer>

      <Typography variant="body1">{t('no_attachments')}</Typography>
    </Container>
  );
};

const AttachmentItem: FC<{
  attachment: AttachmentFile;
  onDelete: () => void;
}> = ({ attachment, onDelete }) => {
  return (
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{
          paddingInline: 2,
          paddingBlock: 1,
          borderRadius: 1,
          borderColor: 'outline',
          borderStyle: 'solid',
          borderWidth: 1,
          width: '100%',
          cursor: 'default',
        }}
      >
        <Stack
          sx={{
            padding: 1.5,
            borderRadius: 1,
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '32px',
            backgroundColor: 'primary.action.selected',
          }}
        >
          <AttachFileOutlined sx={{ color: 'primary.main', fontSize: '20px' }} />
        </Stack>

        <Stack>
          <Typography variant="labelSmall">{attachment.name}</Typography>
          <Typography variant="caption" color="secondary">
            {attachment.contentType}
          </Typography>
        </Stack>
      </Stack>

      <IconButton variant="outlined" color="error" onClick={onDelete}>
        <TrashCanIcon width="24px" height="24px" />
      </IconButton>
    </Stack>
  );
};

export const JobCloseoutModal: FC<JobCloseoutModalProps> = ({
  open,
  onClose,
  onComplete,
  loading = false,
}) => {
  const t = useTranslationPrefix('components.cards.job_closeout_modal');
  const notify = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { uploadFile } = useCaseUploadFile();

  const [securitySealNumber, setSecuritySealNumber] = useState('');
  const [closeoutNotes, setCloseoutNotes] = useState('');
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);

  const handleInputChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      const files = Array.from(event.target.files);
      const fileNames = files.map((f) => f.name);

      setUploadingFiles((prev) => [...prev, ...fileNames]);

      const uploadPromises = files.map(async (file) => {
        try {
          const uploadedFile = await uploadFile({ file });
          setAttachments((prev) => [...prev, uploadedFile]);
          return { success: true, name: file.name };
        } catch (e) {
          notify(t('snackbar.error_file', { fileName: file.name }), {
            variant: 'error',
          });
          return { success: false, name: file.name };
        } finally {
          setUploadingFiles((prev) => prev.filter((name) => name !== file.name));
        }
      });

      const results = await Promise.all(uploadPromises);
      const successCount = results.filter((r) => r.success).length;

      if (successCount > 0) {
        notify(t('snackbar.success_count', { count: successCount }), {
          variant: 'success',
        });
      }

      event.target.value = '';
    },
    [notify, t, uploadFile],
  );

  const handleComplete = useCallback(() => {
    onComplete({
      securitySealNumber: securitySealNumber.trim() || undefined,
      closeoutNotes: closeoutNotes.trim() || undefined,
      attachments,
    });
  }, [onComplete, securitySealNumber, closeoutNotes, attachments]);

  const handleClose = useCallback(() => {
    setSecuritySealNumber('');
    setCloseoutNotes('');
    setAttachments([]);
    setUploadingFiles([]);
    onClose();
  }, [onClose]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('title')}
      maxWidth="sm"
      fullWidth
      content={
        <Stack gap={4}>
          <Stack gap={2}>
            <Typography variant="caption">{t('security_seal_label')}</Typography>
            <TextField
              fullWidth
              value={securitySealNumber}
              onChange={(e) => setSecuritySealNumber(e.target.value)}
              inputProps={{ maxLength: 100 }}
              disabled={loading}
            />
          </Stack>

          <Stack gap={2}>
            <Typography variant="caption">{t('notes_label')}</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              placeholder={t('notes_placeholder')}
              value={closeoutNotes}
              onChange={(e) => setCloseoutNotes(e.target.value)}
              inputProps={{ maxLength: 500 }}
              disabled={loading}
            />
          </Stack>

          <Stack
            sx={{ borderRadius: 1, borderColor: 'outline', borderStyle: 'solid' }}
            p={4}
            gap={2}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" pb={2}>
              <Typography variant="h6">{t('attachments_label')}</Typography>
              <Button
                startIcon={<AttachFileOutlined />}
                size="small"
                variant="outlined"
                disabled={uploadingFiles.length > 0 || loading}
                onClick={() => {
                  fileInputRef.current?.click();
                }}
              >
                {t('btn_add_attachment')}
              </Button>

              <input
                type="file"
                accept={acceptedFiles}
                hidden
                multiple
                onChange={handleInputChange}
                ref={fileInputRef}
              />
            </Stack>

            {uploadingFiles.length > 0 && (
              <Stack spacing={2}>
                {uploadingFiles.map((fileName) => (
                  <Stack
                    key={fileName}
                    direction="row"
                    alignItems="center"
                    gap={2}
                    sx={{
                      paddingInline: 2,
                      paddingBlock: 1,
                      borderRadius: 1,
                      borderColor: 'outline',
                      borderStyle: 'dashed',
                      borderWidth: 1,
                      opacity: 0.7,
                    }}
                  >
                    <CircularProgress size={20} />
                    <Typography variant="labelSmall">{fileName}</Typography>
                    <Typography variant="caption" color="secondary">
                      {t('uploading')}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            {attachments.length === 0 && uploadingFiles.length === 0 ? (
              <NoAttachments />
            ) : (
              <Stack spacing={4}>
                {attachments.map((attachment) => (
                  <AttachmentItem
                    key={attachment.uri}
                    attachment={attachment}
                    onDelete={() => {
                      setAttachments((prev) => prev.filter((a) => a.uri !== attachment.uri));
                    }}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      }
      actions={{
        secondary: {
          text: t('btn_cancel'),
          onClick: handleClose,
          disabled: loading,
        },
        primary: {
          text: t('btn_complete'),
          onClick: handleComplete,
          loading: loading,
          disabled: uploadingFiles.length > 0,
        },
      }}
    />
  );
};
